import DbService from 'resources/db.service';
import {
  USER_TYPES, USER_STATUSES, CLIENT_ROLES,
} from 'resources/user/user.constants';
import chunkfyTask from 'utils/chunkfyTask';

import config from 'config';

import * as storageService from 'services/storage.service';

import schema from './user.schema';

const service = new DbService('users', { schema });

const createRegisteredUser = async ({
  authId,
  companyId,
  email,
}) => {
  let response = await service.create({
    auth_id: authId,
    company_id: companyId,
    email,
    status: USER_STATUSES.REGISTERED,
    env: config.env,
  });

  response = service.processResponse(response);
  if (response.isBadRequest) {
    return null;
  }

  return response.data[0];
};

const createNotInvitedUser = async ({
  firstName,
  lastName,
  email,
  type,
  companyId,
}) => {
  let response = await service.create({
    type,
    status: USER_STATUSES.NOT_INVITED,
    first_name: firstName,
    last_name: lastName,
    email: email.toLowerCase(),
    company_id: companyId,
    env: config.env,
    gmail_integration: type === USER_TYPES.COORDINATOR || type === USER_TYPES.ADMIN,
  });

  response = service.processResponse(response);
  if (response.isBadRequest) {
    return null;
  }

  return response.data[0];
};

const createClient = async ({
  firstName,
  lastName,
  email,
  phoneNumber,
  realtorId,
  companyId,
  isSample,
}) => {
  const data = {
    type: USER_TYPES.CLIENT,
    status: USER_STATUSES.NOT_INVITED,
    roles: [CLIENT_ROLES.NEW_CLIENT],
    first_name: firstName,
    last_name: lastName,
    email: email.toLowerCase(),
    phone_number: phoneNumber,
    realtor_ids: [realtorId],
    company_id: companyId,
    env: config.env,
    gmail_integration: false,
  };

  if (isSample) {
    data.app_state = { is_sample: true };
  }

  let response = await service.create(data);

  response = service.processResponse(response);
  if (response.isBadRequest) {
    return null;
  }

  return response.data[0];
};

const verifyEmail = async (userId) => {
  const user = await service.findById(userId);

  const response = await service
    .updateOne({ id: user.id }, {
      app_state: {
        ...user.app_state,
        is_email_verified: true,
      },
    });

  if (response.isBadRequest) {
    return null;
  }

  return response.data[0];
};

const updateUser = async (userData) => {
  const response = await service
    .updateOne({ id: userData.id }, userData);

  if (response.isBadRequest) {
    return null;
  }

  return response.data[0];
};

const checkIsUserExists = async (email) => {
  const response = await service
    .find({ email });

  return !!response?.length;
};

// -> start getters

const findByEmail = async (email) => {
  const data = await service.find({ email });

  return data?.[0] || null;
};

const getByAuthId = async (id) => {
  const data = await service.find({ auth_id: id });

  return data?.[0] || null;
};

const getUserById = async (userId) => service.findById(userId);

const getUsersByIds = async (ids) => {
  const task = async (chunkIds) => {
    // let response = await service
    //   .select()
    //   .in('id', chunkIds)
    //   .order('created_on', { ascending: true });

    let response = await service.selectByKey({
      selectKey: 'getUsersByIds',
      params: { ids: chunkIds },
    });

    response = service.processResponse(response);

    return response;
  };

  const uniqueIds = [...new Set(ids)];
  const response = await chunkfyTask(uniqueIds, task, 200);

  const data = response?.[0]?.data;

  return data?.length > 0 ? data : null;
};

const getRealtorClients = async (realtorId) => {
  // let response = await service
  //   .select()
  //   .contains('realtor_ids', [realtorId])
  //   .match({
  //     type: USER_TYPES.CLIENT,
  //   });

  let response = await service.selectByKey({
    selectKey: 'getRealtorClients',
    params: { realtorId },
  });

  response = service.processResponse(response);

  return response.data.length > 0 ? response.data : null;
};

const attachAvatarUrl = async (user) => {
  if (user.avatar_url) {
    const avatarUrl = await storageService.getAvatarUrl(user.avatar_url);

    return {
      ...user,
      avatar_url: avatarUrl,
    };
  }

  return user;
};

const getCompanyClients = async (companyId) => {
  const data = await service.find({
    company_id: companyId,
    type: USER_TYPES.CLIENT,
  });

  return data;
};

// Get both team members and clients
const getCompanyUsers = async (companyId) => {
  // let response = await service
  //   .select()
  //   .match({
  //     company_id: companyId,
  //   })
  //   .order('created_on', { ascending: false });

  let response = await service.selectByKey({
    selectKey: 'getCompanyUsers',
  });

  response = service.processResponse(response);

  return response.data.length > 0 ? response.data : null;
};
// <- end getters

export default Object.assign(service, {
  createRegisteredUser,
  createNotInvitedUser,
  createClient,
  updateUser,
  verifyEmail,
  checkIsUserExists,
  // getters
  findByEmail,
  getByAuthId,
  getUserById,
  getUsersByIds,
  getRealtorClients,
  getCompanyClients,
  getCompanyUsers,
  attachAvatarUrl,
});
